import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ApiError } from "./lib/api";
import { fetchMe } from "./lib/auth";
import { AccountDetailPage } from "./pages/AccountDetail";
import { AccountNewPage } from "./pages/AccountNew";
import { AccountsListPage } from "./pages/AccountsList";
import { Allocation } from "./pages/Allocation";
import { BudgetMonthly } from "./pages/BudgetMonthly";
import { CashflowDetail } from "./pages/CashflowDetail";
import { CashflowList } from "./pages/CashflowList";
import { CashflowMonthly } from "./pages/CashflowMonthly";
import { CashflowNew } from "./pages/CashflowNew";
import { CashflowSavingsRate } from "./pages/CashflowSavingsRate";
import { CategoriesList } from "./pages/CategoriesList";
import { DashboardPage } from "./pages/Dashboard";
import { DividendsPage } from "./pages/Dividends";
import { ImportPage } from "./pages/Import";
import { InsuranceDetail } from "./pages/InsuranceDetail";
import { InsuranceList } from "./pages/InsuranceList";
import { InsuranceNew } from "./pages/InsuranceNew";
import { InvestmentDetail } from "./pages/InvestmentDetail";
import { InvestmentNew } from "./pages/InvestmentNew";
import { InvestmentsList } from "./pages/InvestmentsList";
import { LoginPage } from "./pages/Login";
import { Networth } from "./pages/Networth";
import { OtterfolioPage } from "./pages/Otterfolio";
import { PensionDetail } from "./pages/PensionDetail";
import { PensionFire } from "./pages/PensionFire";
import { PensionList } from "./pages/PensionList";
import { PensionNew } from "./pages/PensionNew";
import { RealEstateDetail } from "./pages/RealEstateDetail";
import { RealEstateList } from "./pages/RealEstateList";
import { RealEstateNew } from "./pages/RealEstateNew";
import SettingsPage from "./pages/Settings";
import { SsoBridgePage } from "./pages/SsoBridge";
import { TransactionsList } from "./pages/TransactionsList";
import { WatchlistItemDetail } from "./pages/otterfolio/WatchlistItemDetail";

function useAuthGate() {
  return useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
  });
}

function ProtectedShell({ children }: { children: React.ReactNode }) {
  const me = useAuthGate();
  const location = useLocation();

  if (me.isLoading) {
    return (
      <Layout authenticated={false}>
        <p className="text-slate-500">...</p>
      </Layout>
    );
  }
  if (me.isError) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <Layout authenticated>{children}</Layout>;
}

function BareProtectedShell({ children }: { children: React.ReactNode }) {
  const me = useAuthGate();
  const location = useLocation();

  if (me.isLoading) return null;
  if (me.isError) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Layout authenticated={false}>
            <LoginPage />
          </Layout>
        }
      />
      <Route path="/sso" element={<SsoBridgePage />} />
      <Route
        path="/"
        element={
          <ProtectedShell>
            <DashboardPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/accounts"
        element={
          <ProtectedShell>
            <AccountsListPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/accounts/new"
        element={
          <ProtectedShell>
            <AccountNewPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/accounts/:id"
        element={
          <ProtectedShell>
            <AccountDetailPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/budget"
        element={
          <ProtectedShell>
            <BudgetMonthly />
          </ProtectedShell>
        }
      />
      <Route
        path="/cashflow"
        element={
          <ProtectedShell>
            <CashflowList />
          </ProtectedShell>
        }
      />
      <Route
        path="/cashflow/new"
        element={
          <ProtectedShell>
            <CashflowNew />
          </ProtectedShell>
        }
      />
      <Route
        path="/cashflow/monthly"
        element={
          <ProtectedShell>
            <CashflowMonthly />
          </ProtectedShell>
        }
      />
      <Route
        path="/cashflow/savings-rate"
        element={
          <ProtectedShell>
            <CashflowSavingsRate />
          </ProtectedShell>
        }
      />
      <Route
        path="/cashflow/:id"
        element={
          <ProtectedShell>
            <CashflowDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/networth"
        element={
          <ProtectedShell>
            <Networth />
          </ProtectedShell>
        }
      />
      <Route
        path="/allocation"
        element={
          <ProtectedShell>
            <Allocation />
          </ProtectedShell>
        }
      />
      <Route
        path="/investments"
        element={
          <ProtectedShell>
            <InvestmentsList />
          </ProtectedShell>
        }
      />
      <Route
        path="/investments/new"
        element={
          <ProtectedShell>
            <InvestmentNew />
          </ProtectedShell>
        }
      />
      <Route
        path="/investments/:id"
        element={
          <ProtectedShell>
            <InvestmentDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/dividends"
        element={
          <ProtectedShell>
            <DividendsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/otterfolio"
        element={
          <BareProtectedShell>
            <OtterfolioPage />
          </BareProtectedShell>
        }
      />
      <Route
        path="/otterfolio/watchlist/:id"
        element={
          <BareProtectedShell>
            <WatchlistItemDetail />
          </BareProtectedShell>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedShell>
            <ImportPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/realestate"
        element={
          <ProtectedShell>
            <RealEstateList />
          </ProtectedShell>
        }
      />
      <Route
        path="/insurance"
        element={
          <ProtectedShell>
            <InsuranceList />
          </ProtectedShell>
        }
      />
      <Route
        path="/insurance/new"
        element={
          <ProtectedShell>
            <InsuranceNew />
          </ProtectedShell>
        }
      />
      <Route
        path="/insurance/:id"
        element={
          <ProtectedShell>
            <InsuranceDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/pension"
        element={
          <ProtectedShell>
            <PensionList />
          </ProtectedShell>
        }
      />
      <Route
        path="/pension/new"
        element={
          <ProtectedShell>
            <PensionNew />
          </ProtectedShell>
        }
      />
      <Route
        path="/pension/fire"
        element={
          <ProtectedShell>
            <PensionFire />
          </ProtectedShell>
        }
      />
      <Route
        path="/pension/:id"
        element={
          <ProtectedShell>
            <PensionDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedShell>
            <TransactionsList />
          </ProtectedShell>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedShell>
            <CategoriesList />
          </ProtectedShell>
        }
      />
      <Route
        path="/realestate/new"
        element={
          <ProtectedShell>
            <RealEstateNew />
          </ProtectedShell>
        }
      />
      <Route
        path="/realestate/:id"
        element={
          <ProtectedShell>
            <RealEstateDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedShell>
            <SettingsPage />
          </ProtectedShell>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
